﻿import { autorun } from 'mobx';
import ImageTile from 'ol/ImageTile';
import { get as getProjection } from 'ol/proj';
import TileWMS from "ol/source/TileWMS";
import { createXYZ } from 'ol/tilegrid';
import { IChartConfig } from "../../models/ChartModels";
import { ChartStates } from "../../models/ChartStates";
import { MobxReactionDisposer } from "../../models/MobxReactionDisposer";
import { ChartLayerDetailLevel } from '../../stores/ChartStore';

interface WmsParams {
    LAYERS: string,
    SHALLOWDEPTH: number,
    SAFETYDEPTH: number,
    DEEPDEPTH: number,
    CONTOURINTERVAL: number,
    VERSION: string,
    FORMAT: string,
    TILED: boolean
}

const DEFAULT_LAYER: ChartLayerDetailLevel = 'low';

class MsgWmsSource extends TileWMS {

    protected mobxDisposer = new MobxReactionDisposer();

    constructor(
        config: IChartConfig,
        private states: ChartStates) {

        super({
            ...config.wmsLayer,
            params: {
                LAYERS: DEFAULT_LAYER,
                SHALLOWDEPTH: 0,
                SAFETYDEPTH: 5,
                DEEPDEPTH: 100,
                CONTOURINTERVAL: 0.1,
                VERSION: '1.3.0',
                FORMAT: 'image/png',
                TILED: true
            } as WmsParams,
            serverType: 'geoserver',
            tileGrid: createXYZ({
                extent: getProjection('EPSG:3857')?.getExtent(),
                tileSize: 1024
            })
        });

        const baseUrl = config.wmsLayer?.url;

        this.mobxDisposer.add([
            autorun(() => {
                const token = this.states.token.token;
                if (token && baseUrl) {
                    this.setUrl(baseUrl.replace("{token}", token));
                    this.setTileLoadFunction((tile, url) => {
                        if (tile instanceof ImageTile) {
                            const image = tile.getImage() as HTMLImageElement;
                            image.src = url.replace("{token}", () => {
                                return token || "";
                            });
                        }
                    });
                }
                else {
                    this.setUrl("");
                }
            }),

            autorun(() => {
                if (states.chartWms) {
                    const {
                        showCustomDepthContours, useCustomDepthContours, deepContour, shallowContour, safetyDepth,
                        useCustomDepthContourInterval, showCustomDepthContourInterval, depthContourInterval
                    } = states.chartWms;

                    if (useCustomDepthContours && showCustomDepthContours) {
                        const params: WmsParams = this.getParams();

                        params.DEEPDEPTH = deepContour;
                        params.SHALLOWDEPTH = shallowContour;
                        params.SAFETYDEPTH = safetyDepth;
                        params.CONTOURINTERVAL = (useCustomDepthContourInterval && showCustomDepthContourInterval) ? depthContourInterval : 0.1
                        params.LAYERS = states.chart.layerDetailLevel ?? DEFAULT_LAYER;

                        this.updateParams(params);
                    }
                }
            })
        ])
    }

    public dispose() {
        this.mobxDisposer.clear();
        super.dispose();
    }
}

export default MsgWmsSource;